// Native (Capacitor) shell bootstrap — see MOBILE_APP_PLAN.md (M1).
//
// One job: when the app runs inside the native shell (iOS/Android), wire the
// native chrome — status bar, splash dismissal, hardware back button. It is a
// hard no-op in a web browser (returns before importing any Capacitor plugin),
// so the web bundle is unaffected and these plugin chunks only load on a device.
//
// This file is intentionally product-agnostic: it does NOT decide which shell or
// route the app boots into (that's M1b, pending the platform-vs-Clara product
// call). It only makes the container behave like a native app.

import { Capacitor } from "@capacitor/core";

export async function initNativeShell(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  // Status bar: light glyphs for the usual case, because most shells this boots
  // into put dark chrome at the top (the main app's midnight header, Beaver's
  // charcoal masthead).
  //
  // Style.Dark is NOT a typo. Capacitor names the style after the BACKGROUND it
  // is meant for: Style.Dark = "light text for dark backgrounds". This line said
  // Style.Dark's opposite for a long time while the comment above it claimed
  // "light content" — invisible only because the status bar sat on its own solid
  // band. The moment a barn app went edge-to-edge (overlaysWebView: true) the
  // clock turned dark-on-dark over the masthead. MobileBoot.tsx has always had
  // it right; this now matches.
  //
  // ⚠️ "every shell puts dark chrome at the top" STOPPED BEING TRUE. A barn with
  // `mastheadTone: "light"` hands over to a WHITE masthead, and as of 2026-10-31
  // those barns also boot on a white splash and a white status-bar band — so the
  // unconditional Style.Dark here would paint a WHITE clock on a WHITE band and
  // make it unreadable on every screen. That is the same failure as the Android
  // 15 band bug (playbook gotcha 10), arriving from the opposite direction.
  // Four barns are light today: Coffeyville, Flint Hills, Ogallala, St Onge.
  //
  // Background color is deliberately NOT set here. It is Android-only, and every
  // flavor already declares its own in capacitor.config.ts (main #0B0E13,
  // Beaver #23201C). Hardcoding a third value here overrode both.
  //
  // Resolved BEFORE the plugin import so that a registry failure costs us the
  // tone, not the status bar itself.
  let lightChrome = false;
  try {
    const { activeBarnApp } = await import("@/barnApps/registry");
    lightChrome = activeBarnApp()?.brand.mastheadTone === "light";
  } catch {
    /* not a barn build — keep the dark-chrome default */
  }

  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: lightChrome ? Style.Light : Style.Dark });
  } catch {
    /* plugin unavailable — non-fatal */
  }

  // Dismiss the native splash once React has mounted and painted.
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    // iOS: hide the plugin's splash at once. The plugin draws it inside the
    // web view, which StatusBar overlaysWebView:false pushes below the status
    // bar — so its logo sat ~54 pt lower than the launch screen's and jolted
    // on hand-off (first iPhone run, 2026-10-20). AppDelegate (written by
    // scripts/native-ios-prepare.mjs) instead lays the launch storyboard over
    // the whole window, holds it, and animates it away; this one is never seen.
    await SplashScreen.hide(Capacitor.getPlatform() === "ios" ? { fadeOutDuration: 0 } : undefined);
  } catch {
    /* non-fatal */
  }

  // Android hardware back button → browser history; exit the app at the root
  // so back doesn't strand the user on a blank screen.
  try {
    const { App } = await import("@capacitor/app");
    App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });

    // The main app's sign-in return address (com.raisedbeef.app://auth): a
    // Google or Apple sign-in run in the browser sheet comes back here, warm
    // (appUrlOpen) or cold (the launch URL). Only that scheme is looked at, and
    // the handler is loaded only then, so the barn apps are untouched.
    const onUrl = (url?: string | null) => {
      if (!url || !url.startsWith("com.raisedbeef.app://")) return;
      void import("@/rbapp/appStore").then((m) => m.handleAuthDeepLink(url));
    };
    App.addListener("appUrlOpen", ({ url }) => onUrl(url));
    App.getLaunchUrl().then((l) => onUrl(l?.url)).catch(() => {});
  } catch {
    /* non-fatal */
  }
}
